import { samplesConfigObj } from '@/components/Glossary/Sections/SampleTypes/SamplesConfig';
import { DateTimeEditor } from '@/components/UI/SpreadSheet/editors/DateTimeEditor';
import type {
  CustomColumnDataGetterSetterFunction,
  CustomColumnSettings,
  CustomGridSettings,
} from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { _isEmptyString, _isNil, _isNotDeepEqual, _isNotEmpty, _isNumber, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { MetadataField } from '@/model/Metadata.model';
import type { SampleEdit, SampleMeasurementKey } from '@/model/Sample.model';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import { ApiService } from '@/support/ApiService';
import { createMetaMap, defaultMetadataToColumnSettingMapper } from '@/support/hot';
import { DateUtils } from '@/utils/Date.utils';
import type { CellValue, RowObject } from 'handsontable/common';
import type { CellProperties, ColumnSettings } from 'handsontable/settings';
import type { CustomSample } from './BulkSampleEdit';

// Type guard to check if a string is a valid SampleMeasurementKey
const isSampleMeasurementKey = (key: string): key is SampleMeasurementKey => {
  return ['weight', 'width', 'length', 'depth', 'volume'].includes(key);
};

export type ColumnWrapper = Pick<CustomGridSettings, 'columns' | 'colHeaders'>;

export const getMetaColumns = (
  selectedRows: Array<CustomSample> | undefined = [],
  metadata: Array<MetadataField>
): Array<MetadataField> => {
  const glossaryIDs = selectedRows?.reduce<Array<ID>>((ids, row) => {
    if (row.metadata) {
      const metas = row.metadata.filter((m) => !ids.includes(m.glossary_id)).map((m) => m.glossary_id);
      ids = [...ids, ...metas];
    }
    return ids;
  }, []);
  if (_isNotEmpty(metadata) && _isNotEmpty(glossaryIDs)) {
    return metadata.filter((data) => glossaryIDs.includes(data.id));
  }
  return [];
};

export const baseColumnData = (sampleType?: TeamGlossaryV1, plannedSamplesEnabled = false): ColumnWrapper => {
  const columns: Array<CustomColumnSettings> = [
    {
      data: 'api_id',
      type: 'text',
      readOnly: true,
      value: 'Sample API ID',
      width: 150,
    },
    {
      data: 'sample_id',
      type: 'text',
      value: 'Sample ID',
      allowInvalid: true,
      // Ideally this could be done in a batch, but once again HoT strikes down any sense
      // Realistically there is a max of 50 edited at a single time so this solution
      // will suffice for now until HoT gets ripped out
      validator: function (value: string, callback: (valid: boolean) => void) {
        return isSampleIdUniqueInTeam(this as CellProperties, value, callback);
      },
      validationErrorMessage: (value: unknown) => 'Sample ID is required and must be unique in team',
      width: 150,
    },
    {
      data: 'subject.name',
      readOnly: true,
      type: 'text',
      value: 'Animal name',
      width: 150,
    },
    {
      data: 'type',
      readOnly: true,
      type: 'text',
      value: 'Sample type',
      width: 150,
    },
    {
      data: 'date',
      validator: function (value: string, callback: (valid: boolean) => void) {
        const cell = this as CellProperties;
        const sample = cell.instance.getSourceDataAtRow(cell.row) as CustomSample;
        const isCollected = sample?.status === 'collected';

        // Only collected samples require a date
        if (isCollected) {
          callback(DateUtils.isValidDateTime(value));
        } else {
          // For planned/cancelled samples, date is optional but must be valid if provided
          callback(_isNil(value) || _isEmptyString(value) || DateUtils.isValidDateTime(value));
        }
      },
      type: DateTimeEditor.type,
      value: 'Date taken',
      width: 200,
      validationErrorMessage: () => 'Collected samples require a valid collected date',
    },
    ...(plannedSamplesEnabled
      ? [
          {
            data: timepointAccessor,
            type: 'timepoint',
            singleValue: true,
            allow_zero: true,
            allow_negative: true,
            value: 'Timepoint',
            width: 150,
          } as CustomColumnSettings,
        ]
      : []),
    {
      data: 'comments',
      type: 'text',
      value: 'Comments',
      width: 200,
      validator: function (value: string, callback: (valid: boolean) => void) {
        callback((value ?? '').length <= 255);
      },
      validationErrorMessage: () => 'Comments have a max length of 255 characters',
    },
    ...(sampleType?.options?.details?.filter(isSampleMeasurementKey).flatMap<CustomColumnSettings>((type) => [
      {
        data: detailValueSetter(type),
        value: `${type.slice(0, 1).toUpperCase()}${type.slice(1)} Value`,
        type: 'numeric',
        validator: function (this: CellProperties, value: string, callback: (valid: boolean) => void) {
          callback(detailValueValidator(value));
        },
        width: 150,
        validationErrorMessage: () => 'Value must be numeric',
      },
      {
        data: detailUnitSetter(type),
        value: `${type.slice(0, 1).toUpperCase()}${type.slice(1)} Unit`,
        type: 'dropdown',
        source: samplesConfigObj[type].units,
        width: 150,
        validator: function (this: CellProperties, value: string, callback: (valid: boolean) => void) {
          callback(detailUnitValidator(type, samplesConfigObj[type].units, this, value));
        },
        validationErrorMessage: () => 'Unit is required when a value is present and must be a valid option',
      },
    ]) ?? []),
  ];

  return { columns, colHeaders: columns.map((c) => c.value) };
};

const isSampleIdUniqueInTeam = async (cell: CellProperties, sampleId: string, callback: (valid: boolean) => void) => {
  if (_isNil(sampleId)) {
    callback(false);
    return;
  }

  // This is generally hateful but we can thank HoT for having a ludicrous way of storing data references
  // HoT stores the data as a 3D array, if something has broken here check the column order matches the
  // following numbers
  const sampleApiIdColumn = 0;
  const sampleIdColumn = 1;

  const cellData = cell.instance.getData();
  cellData.splice(cell.row, 1);

  if (cellData.some((sample: Record<string, string>) => sample[sampleIdColumn] === sampleId)) {
    callback(false);
  } else {
    const result = await ApiService.call({
      endpoint: 'GET /api/v1/samples/{sampleApiId}/unique/{sampleId}',
      path: { sampleApiId: cell.instance.getData()[cell.row][sampleApiIdColumn], sampleId },
    });
    callback(result.type === 'success' ? (result.body.success ?? false) : false);
  }
};

/**
 * Validates sample measurement values. Empty values are allowed, non-empty values must be numeric.
 * @param value - The measurement value to validate
 * @returns True if value is empty or numeric, false otherwise
 */
const detailValueValidator = (value: string): boolean =>
  _isNil(value) || _isEmptyString(value) || _isNumber(Number(value));

/**
 * Validates sample measurement units. Units can be set without values, but values require units.
 * Accesses row data directly to check if a value exists for the measurement.
 * @param type - The measurement type (weight, volume, width, length, depth)
 * @param units - Array of valid unit strings for this measurement type
 * @param cell - Handsontable cell properties containing row data
 * @param unit - The unit value to validate
 * @returns True if validation passes, false otherwise
 */
const detailUnitValidator = (
  type: SampleMeasurementKey,
  units: Array<string>,
  cell: CellProperties,
  unit: string
): boolean => {
  const rowData = cell.instance.getSourceDataAtRow(cell.row) as CustomSample;
  const valueData = rowData?.details?.[type]?.value;
  const isEmpty = _isNil(unit) || _isEmptyString(unit);

  return isEmpty ? _isNil(valueData) || _isEmptyString(valueData) : units.includes(unit);
};

const detailValueSetter = (type: SampleMeasurementKey) => (row: RowObject, value: CellValue) => {
  const currentDetails = (row as CustomSample)?.details?.[type];
  if (_notNil(currentDetails) && typeof value != 'undefined') {
    row.details[type].value = value;
  } else if (_isNil(currentDetails) && typeof value != 'undefined') {
    row.details[type] = {
      value,
      key: type,
    };
  }
  return currentDetails?.value;
};

const detailUnitSetter = (type: SampleMeasurementKey) => (row: RowObject, value: CellValue) => {
  const currentDetails = (row as CustomSample)?.details?.[type];
  if (_notNil(currentDetails) && typeof value != 'undefined') {
    row.details[type].unit = value;
  } else if (_isNil(currentDetails) && typeof value != 'undefined') {
    row.details[type] = {
      unit: value,
      key: type,
    };
  }
  return currentDetails?.unit;
};

export const timepointAccessor: CustomColumnDataGetterSetterFunction = function (
  row: RowObject | CellValue[],
  value?: CellValue
): CellValue {
  const sample = row as CustomSample;

  if (arguments.length > 1) {
    if (Array.isArray(value)) {
      sample.timepoint = value.length > 0 ? value[0] : '';
    } else if (typeof value === 'string') {
      sample.timepoint = value;
    } else if (_isNil(value)) {
      sample.timepoint = '';
    }
  }

  return sample.timepoint ? [sample.timepoint] : [];
};

export const getBaseSettings = (plannedSamplesEnabled = false): CustomGridSettings => ({
  ...baseColumnData(undefined, plannedSamplesEnabled),
  // Hides the API ID column, would be good to remove this once fully moved from DB IDs
  hiddenColumns: { columns: [0] },
  columnSorting: false,
  className: 'htMiddle',
  manualColumnResize: true,
  contextMenu: false,
  rowHeights: 50,
  height: 500,
  rowHeaders: true,
});

export const generateColumnData = (
  displayedSampleMetadata: Array<MetadataField>,
  sampleType?: TeamGlossaryV1,
  plannedSamplesEnabled = false
) =>
  displayedSampleMetadata.reduce<ColumnWrapper>(
    ({ columns, colHeaders }, metadata) => {
      if (metadata.field_type) {
        const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapper(metadata);

        return {
          colHeaders: [...(colHeaders as Array<string>), columnHeader],
          columns: [...(columns as Array<CustomColumnSettings>), columnSettings],
          // Hides the API ID column, would be good to remove this once fully moved from DB IDs
          hiddenColumns: { columns: [0] },
        };
      }
      return {
        colHeaders,
        columns,
        // Hides the API ID column, would be good to remove this once fully moved from DB IDs
        hiddenColumns: { columns: [0] },
      };
    },
    baseColumnData(sampleType, plannedSamplesEnabled)
  );

export const generateSampleBulkUpdateList = (
  initialSamples: Array<CustomSample> = [],
  editedSamples: Array<CustomSample> = [],
  columns: Array<ColumnSettings>
) => {
  const initialSamplesMap = new Map(initialSamples.map((group) => [group.id, group]));

  const updates = editedSamples.reduce<SampleEdit>((acc, sample) => {
    const { id, api_id, details, date, sample_id, comments, meta, timepoint } = sample;
    // Compares the initial samples against the new, only sending changed rows
    // Would be good to build on this to only send the exact properties changed, but
    // this is a limitation of the current deep equals

    if (_isNotDeepEqual(initialSamplesMap.get(id), sample)) {
      const payload = {
        api_id: api_id,
        details: Object.values(details),
        date: date,
        sample_id: sample_id,
        comments: comments,
        meta: createMetaMap(meta, columns),
        timepoint: timepoint || null,
      };

      acc.push(payload);
    }
    return acc;
  }, []);

  return updates;
};
